import { useMemo } from 'react';
import { Boxes } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Chip } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { formatMoney, formatDate } from '@/lib/format';
import {
  useServiceConfigoptions,
  useServiceAddonConfigoptions,
  type ServiceDetail,
} from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { buildVCloudGroups, parseVMConfig, isStorage, type VCloudGroup } from './vcloud-config-helpers';

const muted = { color: 'var(--vn-fg-muted)' } as const;
const nf = new Intl.NumberFormat('en-US');
const semibold: React.CSSProperties = {
  fontWeight: 'var(--vn-font-weight-semibold)' as React.CSSProperties['fontWeight'],
};
const rightCell: React.CSSProperties = {
  display: 'block',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
const R = (children: React.ReactNode) => <span style={rightCell}>{children}</span>;
const num = (v?: number) => (v && v > 0 ? nf.format(v) : '');

const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success', trialing: 'info', 'waiting-confirm': 'info', 'waiting-create-service': 'warning',
  'on-resubmit-information': 'warning', 'pending-terminated': 'danger', rejected: 'danger', cancelled: 'neutral',
};
const statusChip = (s?: string) => (s ? <Chip appearance="subtle" tone={STATUS_TONE[s] ?? 'neutral'} size="sm">{s.replace(/-/g, ' ')}</Chip> : null);
const cycle = (a?: string | null, b?: string | null) => (a || b ? `${formatDate(a)} → ${formatDate(b)}` : <span style={muted}>—</span>);

/** Committed total for a group: register price for package pricing, else summed line totals. */
const groupTotal = (g: VCloudGroup) => (g.pricingModel === 'package' ? g.priceRegister ?? 0 : g.totals.price);

type VItem = VCloudGroup['types'][number]['items'][number];

/** Tree node: service group → VM-type subheader → item, plus a trailing grand-total row. */
interface VNode {
  id: string;
  kind: 'group' | 'type' | 'item' | 'grand';
  label?: string;
  g?: VCloudGroup;
  item?: VItem;
  isOther?: boolean;
  subRows?: VNode[];
}

/**
 * VCloud Information config table — a hierarchical resource view (service → VM
 * type → item) with rolled-up vCores/RAM/storage and per-group commercial totals,
 * mirroring legacy AuditCloud. Built on the @ui DataTable tree mode
 * (`getSubRows`); addon services expand under their own header; the grand-total
 * row is the trailing node so its column values stay aligned.
 */
export function VCloudConfigTable({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const currency = detail.service_currency || 'VND';
  const base = useServiceConfigoptions(id);
  const addons = useServiceAddonConfigoptions(id);

  const { groups, grand } = useMemo(
    () => buildVCloudGroups(detail, base.data?.results ?? [], addons.data?.results ?? []),
    [detail, base.data, addons.data],
  );

  const showStorage = groups.some((g) => g.totals.storage > 0);
  const showPublicIP = groups.some((g) => g.totals.publicIP > 0);
  const showFloatingIP = groups.some((g) => g.totals.floatingIP > 0);
  const showBandwidth = groups.some((g) => g.totals.bandwidth > 0);

  const data = useMemo<VNode[]>(() => {
    const nodes: VNode[] = groups.map((g) => ({
      id: `g-${g.serviceId}`,
      kind: 'group',
      g,
      subRows: g.types.map((tg) => ({
        id: `t-${g.serviceId}-${tg.key}`,
        kind: 'type',
        label: tg.label,
        subRows: tg.items.map((item) => ({
          id: `i-${item.service_configoption_id}`,
          kind: 'item',
          item,
          g,
          isOther: tg.key === 'other_service',
        })),
      })),
    }));
    if (groups.length) nodes.push({ id: 'grand', kind: 'grand' });
    return nodes;
  }, [groups]);

  const columns = useMemo<ColumnDef<VNode, unknown>[]>(() => {
    const cols: ColumnDef<VNode, unknown>[] = [
      {
        id: 'label',
        header: 'Service / Item',
        cell: ({ row }) => {
          const n = row.original;
          if (n.kind === 'group') {
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', ...semibold }}>
                {!n.g!.isMain && (
                  <span style={{ fontSize: 'var(--vn-font-size-xs)', textTransform: 'uppercase', color: 'var(--vn-fg-muted)' }}>Addon</span>
                )}
                <span>{n.g!.serviceName}</span>
              </span>
            );
          }
          if (n.kind === 'type') {
            return <span style={{ textTransform: 'uppercase', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>{n.label}</span>;
          }
          if (n.kind === 'grand') return <span style={semibold}>Total</span>;
          const qty = n.item!.commitment_value || 0;
          return <span>{n.item!.name}{qty > 1 ? ` (x${qty})` : ''}</span>;
        },
      },
      {
        id: 'vCores',
        header: 'vCores',
        cell: ({ row }) => {
          const n = row.original;
          if (n.kind === 'group') return R(num(n.g!.totals.vCores));
          if (n.kind === 'grand') return R(num(grand.vCores));
          if (n.kind === 'item') return R(!n.isOther ? num(parseVMConfig(n.item!.name).cores) : '');
          return R('');
        },
      },
      {
        id: 'ram',
        header: 'RAM',
        cell: ({ row }) => {
          const n = row.original;
          if (n.kind === 'group') return R(num(n.g!.totals.ram));
          if (n.kind === 'grand') return R(num(grand.ram));
          if (n.kind === 'item') return R(!n.isOther ? num(parseVMConfig(n.item!.name).ram) : '');
          return R('');
        },
      },
    ];

    if (showStorage) {
      cols.push({
        id: 'storage',
        header: 'Storage',
        cell: ({ row }) => {
          const n = row.original;
          if (n.kind === 'group') return R(num(n.g!.totals.storage));
          if (n.kind === 'grand') return R(num(grand.storage));
          if (n.kind === 'item') {
            const qty = n.item!.commitment_value || 0;
            return R(n.isOther && isStorage(n.item!.type, n.item!.name) ? num(qty) : '');
          }
          return R('');
        },
      });
    }
    const totalsOnly = (pick: (g: VCloudGroup['totals']) => number, grandVal: number) =>
      ({ row }: { row: { original: VNode } }) => {
        const n = row.original;
        if (n.kind === 'group') return R(num(pick(n.g!.totals)));
        if (n.kind === 'grand') return R(num(grandVal));
        return R('');
      };
    if (showPublicIP) cols.push({ id: 'publicIP', header: 'Public IP', cell: totalsOnly((t) => t.publicIP, grand.publicIP) });
    if (showFloatingIP) cols.push({ id: 'floatingIP', header: 'Floating IP', cell: totalsOnly((t) => t.floatingIP, grand.floatingIP) });
    if (showBandwidth) cols.push({ id: 'bandwidth', header: 'Bandwidth', cell: totalsOnly((t) => t.bandwidth, grand.bandwidth) });

    cols.push(
      {
        id: 'unitPrice',
        header: 'Unit price',
        cell: ({ row }) => {
          const n = row.original;
          if (n.kind !== 'item') return R('');
          return R(n.g!.pricingModel === 'package' ? '' : formatMoney(n.item!.commitment_price_per_unit, currency));
        },
      },
      {
        id: 'total',
        header: 'Total',
        cell: ({ row }) => {
          const n = row.original;
          if (n.kind === 'group') return <span style={{ ...rightCell, ...semibold }}>{formatMoney(groupTotal(n.g!), currency)}</span>;
          if (n.kind === 'grand') return <span style={{ ...rightCell, ...semibold }}>{formatMoney(grand.combined, currency)}</span>;
          if (n.kind === 'item') {
            const qty = n.item!.commitment_value || 0;
            const total = n.item!.commitment_price_total || (n.item!.commitment_price_per_unit || 0) * qty;
            return R(formatMoney(total, currency));
          }
          return R('');
        },
      },
      {
        id: 'saleCommission',
        header: 'Sale Commission',
        cell: ({ row }) =>
          row.original.kind === 'group'
            ? row.original.g!.employeeNames?.filter(Boolean).join(', ') || <span style={muted}>—</span>
            : null,
      },
      {
        id: 'startDate',
        header: 'Start date',
        cell: ({ row }) => (row.original.kind === 'group' ? R(formatDate(row.original.g!.startDate)) : null),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (row.original.kind === 'group' ? statusChip(row.original.g!.serviceStatus) : null),
      },
      {
        id: 'activeCycle',
        header: 'Active cycle',
        cell: ({ row }) =>
          row.original.kind === 'group' ? R(cycle(row.original.g!.currentStart, row.original.g!.currentEnd)) : null,
      },
      {
        id: 'renewCycle',
        header: 'Renew cycle',
        cell: ({ row }) => {
          const n = row.original;
          if (n.kind !== 'group') return null;
          return R(n.g!.serviceStatus === 'active' ? cycle(n.g!.continueStart, n.g!.continueEnd) : <span style={muted}>—</span>);
        },
      },
    );
    return cols;
  }, [grand, showStorage, showPublicIP, showFloatingIP, showBandwidth, currency]);

  if (base.isError) return <TabError title="Failed to load configuration" onRetry={() => base.refetch()} />;
  if (base.isLoading) return <TabLoading rows={4} />;

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
      </CardHeader>
      <CardBody>
        {groups.length ? (
          <DataTable
            columns={columns}
            data={data}
            getRowId={(r) => r.id}
            getSubRows={(r) => r.subRows}
            defaultAllExpanded
            enableSorting={false}
          />
        ) : (
          <EmptyState icon={<Boxes size={24} />} title="Pay as you go" description="No committed resources — this service is billed on usage." />
        )}
      </CardBody>
    </Card>
  );
}
